import { useEffect, useRef, useState } from 'react'

// SHA baked into the bundle at build time (see vite.config.ts + build.sh).
// This is the SHA of the source the running app was compiled from.
const RUNNING_SHA = typeof __BUILD_GIT_SHA__ !== 'undefined' ? __BUILD_GIT_SHA__ : 'unknown'

// ISO build time baked into the bundle (vite.config.ts define). Used by the
// force_before kill-switch comparison below.
const RUNNING_BUILT = typeof __BUILD_TIMESTAMP__ !== 'undefined' ? __BUILD_TIMESTAMP__ : 'unknown'

const VERSION_URL = '/vet-portal/VERSION.txt'
const POLL_INTERVAL_MS = 5 * 60 * 1000 // 5 minutes
const FETCH_TIMEOUT_MS = 5000

// A tab that has ignored the update nudge for this long gets the blocking
// force-reload dialog instead. Rationale: on 2026-07-09 a 13-day-stale bundle
// with a since-fixed infinite refetch loop fired 13 req/s at prod and stalled
// logins fleet-wide (see ada-core memory login-stall-vetpo-storm-root-cause).
// Long-stale bundles aren't just missing features — they can carry known
// fixed bugs that actively harm the platform. 24h keeps same-day deploys
// nudge-only (no mid-shift interruptions) while capping how old a running
// bundle can get.
export const STALE_FORCE_MS = 24 * 60 * 60 * 1000

export interface VersionInfo {
  sha_short?: string
  sha?: string
  built?: string
  tag?: string
  version?: string
  /**
   * Optional server-side kill-switch: ISO timestamp. Bundles BUILT BEFORE
   * this instant must force-reload immediately (no 24h grace). Not written
   * by the deploy script — an operator appends it to VERSION.txt on the web
   * hosts when a specific old bundle is misbehaving in the field:
   *   ssh ubuntu@webN "echo 'force_before=2026-07-09T00:00:00Z' | sudo tee -a /var/www/html/vetpo/VERSION.txt"
   * (both hosts; remove the line once the incident passes).
   */
  force_before?: string
}

export function parseVersionTxt(text: string): VersionInfo {
  const out: VersionInfo = {}
  for (const line of text.split('\n')) {
    const eq = line.indexOf('=')
    if (eq < 1) continue
    const key = line.slice(0, eq).trim()
    const value = line.slice(eq + 1).trim()
    if (key === 'sha_short') out.sha_short = value
    else if (key === 'sha') out.sha = value
    else if (key === 'built') out.built = value
    else if (key === 'tag') out.tag = value
    else if (key === 'version') out.version = value
    else if (key === 'force_before') out.force_before = value
  }
  return out
}

/**
 * The escalation rule, pure for testability. Force a blocking reload when an
 * update is available AND either:
 *  - the running bundle was built before the operator-set force_before
 *    cutoff (incident kill-switch), or
 *  - the tab has been continuously stale for STALE_FORCE_MS (auto-escalation
 *    so no tab quietly runs a weeks-old bundle forever).
 * Unknown/garbled timestamps never force — fail toward the nudge, not toward
 * reloading someone mid-work on bad data.
 */
export function shouldForceReload(args: {
  updateAvailable: boolean
  runningBuiltIso: string
  forceBeforeIso?: string
  firstStaleAtMs: number | null
  nowMs: number
}): boolean {
  const { updateAvailable, runningBuiltIso, forceBeforeIso, firstStaleAtMs, nowMs } = args
  if (!updateAvailable) return false
  if (forceBeforeIso) {
    const cutoff = Date.parse(forceBeforeIso)
    const built = Date.parse(runningBuiltIso)
    if (Number.isFinite(cutoff) && Number.isFinite(built) && built < cutoff) {
      return true
    }
  }
  return firstStaleAtMs !== null && nowMs - firstStaleAtMs >= STALE_FORCE_MS
}

async function fetchDeployedVersion(): Promise<VersionInfo | null> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
  try {
    // Cache-bust query in case any intermediate proxy ignores no-cache headers.
    // The deployed VERSION.txt itself is served with no-cache by nginx (see
    // /etc/nginx/sites-enabled/prod-80), so this is belt-and-braces.
    const url = `${VERSION_URL}?_=${Date.now()}`
    const res = await fetch(url, {
      signal: controller.signal,
      cache: 'no-store',
      // Must send cookies — the /vet-portal/ root in nginx is cookie-switched
      // (ENV=beta → beta tree). Without 'same-origin' the request is cookieless
      // and we always get the prod VERSION.txt, which falsely flags every beta
      // user as "update available" forever (clicking Refresh re-runs the same
      // mismatch). See /tmp/issue.png from 2026-05-02.
      credentials: 'same-origin',
    })
    if (!res.ok) return null
    const text = await res.text()
    return parseVersionTxt(text)
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}

export interface VersionCheckState {
  /** SHA the running bundle was built from (short form) */
  runningSha: string
  /** SHA currently deployed on the server (short form), null until first poll */
  deployedSha: string | null
  /** True when running != deployed and we have a confident comparison */
  updateAvailable: boolean
  /**
   * True when the staleness has escalated past the nudge: operator
   * force_before kill-switch hit, or continuously stale > STALE_FORCE_MS.
   * UI must show the blocking ForceReloadDialog.
   */
  forceReload: boolean
  /** Full deployed VERSION info, useful for tooltips/diagnostics */
  deployed: VersionInfo | null
}

/**
 * Polls /vet-portal/VERSION.txt on mount and every 5 minutes while the tab is
 * active. Returns whether a newer build is on the server than what's running
 * in this tab. UI consumes this to nudge users to refresh — preventing the
 * stale-bundle reload-cascade that bit Henry on 2026-04-25.
 *
 * Why this matters even with nginx no-cache: long-lived tabs (user opens
 * VetPo Friday, comes back Monday) never re-fetch index.html. They keep
 * running the Friday bundle until they navigate or refresh. This hook
 * surfaces the staleness so they know to refresh.
 */
export function useVersionCheck(): VersionCheckState {
  const [deployed, setDeployed] = useState<VersionInfo | null>(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true

    const check = async () => {
      const info = await fetchDeployedVersion()
      if (isMountedRef.current && info) {
        setDeployed(info)
      }
    }

    // Skip entirely in dev (RUNNING_SHA === 'unknown') — no SHA to compare against,
    // and the dev server doesn't serve VERSION.txt anyway.
    if (RUNNING_SHA === 'unknown') {
      return () => {
        isMountedRef.current = false
      }
    }

    // Initial check on mount
    check()

    // Periodic poll while tab exists
    const intervalId = window.setInterval(check, POLL_INTERVAL_MS)

    // Also re-check when tab regains focus (cheap, catches the common case
    // of "user came back to a tab they left open overnight")
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        check()
      }
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      isMountedRef.current = false
      window.clearInterval(intervalId)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const deployedSha = deployed?.sha_short ?? null
  // Only flag updateAvailable when both sides are known and differ. Don't
  // false-positive on dev builds, network failures, or unparseable VERSION.txt.
  const updateAvailable =
    deployedSha !== null && RUNNING_SHA !== 'unknown' && deployedSha !== RUNNING_SHA

  // When did this tab FIRST observe staleness? Drives the 24h auto-escalation.
  // Ref (not state): every poll setDeployed()s a fresh object, so the hook
  // re-renders ~5-minutely anyway and the boundary is re-evaluated then.
  const firstStaleAtRef = useRef<number | null>(null)
  useEffect(() => {
    if (updateAvailable && firstStaleAtRef.current === null) {
      firstStaleAtRef.current = Date.now()
    } else if (!updateAvailable) {
      firstStaleAtRef.current = null
    }
  }, [updateAvailable])

  const forceReload = shouldForceReload({
    updateAvailable,
    runningBuiltIso: RUNNING_BUILT,
    forceBeforeIso: deployed?.force_before,
    firstStaleAtMs: firstStaleAtRef.current,
    nowMs: Date.now(),
  })

  return {
    runningSha: RUNNING_SHA,
    deployedSha,
    updateAvailable,
    forceReload,
    deployed,
  }
}
